import { AccessSpace } from '@upright/common/iam/access/types';
import { Dictionary } from 'lodash';
import { getAllEnumValues } from '../../../utils';
import { CompanyAlternativeName } from './alternative-names/types';
import { CompanyComment } from './comments/types';
import { CompanyEmployeeCount } from './data-points/employee-counts/types';
import { CompanyIncome } from './data-points/incomes/types';
import { CompanyRevenue } from './data-points/revenues/types';
import { CompanyDescription } from './descriptions/types';
import { CompanyExtraData } from './extra-data/types';
import { CompanyProductSet } from './products/types';
import { CompanyRelation } from './relations/types';
import { CompanySecUrl } from './sec/types';
import { SecurityIdentifier } from './security-identifier/types';
import { CompanyWikiPage } from './wiki-pages/types';

export interface AuthorMetadata {
  modifiedBy: string;
  createdBy: string;
}

export interface CommonCompanyMetadata {
  createdAt: string;
  updatedAt: string;
}

export enum CompanyStatus {
  ACTIVE = 'ACTIVE',
  DRAFT = 'DRAFT', // Company created but not yet completed (used for payment flow)
  INACTIVE_ACQUIRED = 'INACTIVE_ACQUIRED',
  INACTIVE_BANKRUPTED = 'INACTIVE_BANKRUPTED',
  INACTIVE_OTHER = 'INACTIVE_OTHER',
}

export const allCompanyStatuses = [
  CompanyStatus.ACTIVE,
  CompanyStatus.DRAFT,
  CompanyStatus.INACTIVE_ACQUIRED,
  CompanyStatus.INACTIVE_BANKRUPTED,
  CompanyStatus.INACTIVE_OTHER,
];

/**
 * Helper for determining if a company can be completed in the DMA consultant
 * flow. Currently this is equivalent to checking that the company is in
 * DRAFT status, but centralising the check here keeps backend and frontend
 * logic consistent.
 *
 * Accepts either:
 * - a status enum value
 * - any object with an optional `status` field (e.g. API data, Sequelize model)
 */
export function canCompleteCompany(
  companyOrStatus: { status?: CompanyStatus | null } | CompanyStatus | null | undefined,
): boolean {
  const status = typeof companyOrStatus === 'string' ? companyOrStatus : (companyOrStatus?.status ?? null);
  return status === CompanyStatus.DRAFT;
}

// These values are used on data import sheets. When editing/deleting existing or adding new ones,
// please do a text search in our Drive with the enum values and update any import instructions that mention these.
export enum CompanyType {
  COMPANY = 'COMPANY',
  BUSINESS_UNIT = 'BUSINESS_UNIT',
  NOT_A_COMPANY = 'NOT_A_COMPANY',
  GREEN_BOND = 'GREEN_BOND',
  SOCIAL_BOND = 'SOCIAL_BOND',
  SUSTAINABLE_BOND = 'SUSTAINABLE_BOND',
  SUSTAINABILITY_LINKED_BOND = 'SUSTAINABILITY_LINKED_BOND',
  PUBLIC_SECTOR = 'PUBLIC_SECTOR',
  CASH = 'CASH',
  COMMODITY = 'COMMODITY',
  CUSTOMER_SCENARIO = 'CUSTOMER_SCENARIO', // Company is used to model customer scenarios such as DMA freemium companies. Such companies are not shown in search.
}

export const allCompanyTypes = getAllEnumValues(CompanyType, 'string');
export const allBondCompanyTypes = [
  CompanyType.GREEN_BOND,
  CompanyType.SOCIAL_BOND,
  CompanyType.SUSTAINABLE_BOND,
  CompanyType.SUSTAINABILITY_LINKED_BOND,
];

// Company types that can be excluded from the denominator when calculating coverage statistics
export const companyTypesNotIncludedInCoverage = [
  CompanyType.CASH,
  CompanyType.PUBLIC_SECTOR,
  CompanyType.COMMODITY,
  CompanyType.BUSINESS_UNIT,
  CompanyType.NOT_A_COMPANY,
];

export const companyTypesExcludedFromEuTAggregation = [
  CompanyType.PUBLIC_SECTOR,
  CompanyType.COMMODITY,
  CompanyType.BUSINESS_UNIT,
  CompanyType.NOT_A_COMPANY,
];

export function isCompanyTypeExcludedFromEuTAggregation(companyType: CompanyType | undefined) {
  if (companyType === undefined) {
    return false;
  }
  return companyTypesExcludedFromEuTAggregation.includes(companyType);
}

export function isPrivateCompanyType(companyType: CompanyType | undefined) {
  // Company types were used to mark custom, non-sharable companies,
  // and this distinction is loosely kept for now to mitigate human errors.
  return (
    companyType === CompanyType.NOT_A_COMPANY ||
    companyType === CompanyType.BUSINESS_UNIT ||
    companyType === CompanyType.CUSTOMER_SCENARIO
  );
}

export function isBondCompany(companyType: CompanyType | undefined) {
  if (companyType === undefined) {
    return false;
  }
  return allBondCompanyTypes.includes(companyType);
}

export function isCompanyTypeExcludedFromCoverage(companyType: CompanyType | undefined) {
  if (companyType === undefined) {
    return false;
  }
  return companyTypesNotIncludedInCoverage.includes(companyType);
}

export function isCompanyType(companyType: string | undefined): companyType is CompanyType {
  if (!companyType) return false;
  return allCompanyTypes.includes(companyType as CompanyType);
}

export enum CompanyListingStatus {
  PUBLIC = 'PUBLIC',
  PRIVATE = 'PRIVATE',
}

export const allCompanyListingStatuses = [CompanyListingStatus.PRIVATE, CompanyListingStatus.PUBLIC];

export enum CompanyImpactDriverType {
  BASIC = 'basic', // basic (company whose impact is driven by the products and services it produces and sells)
  INVESTMENT = 'investment', // investment company (company whose impact is largely driven by products and services of companies it invests in)
  PROFESSIONAL_SERVICES = 'prof_serv', // professional services company  (company whose impact is largely driven by products and services of its downstream client companies)
  RETAIL = 'retail', // retail/wholesale company (company whose impact is driven by products and services of its upstream goods)
}

export const allCompanyImpactDriverTypes = [
  CompanyImpactDriverType.BASIC,
  CompanyImpactDriverType.INVESTMENT,
  CompanyImpactDriverType.PROFESSIONAL_SERVICES,
  CompanyImpactDriverType.RETAIL,
];

export interface Company extends CommonCompanyMetadata, AuthorMetadata {
  id: string;
  name: string;
  matchingName: string;
  status: CompanyStatus;
  type: CompanyType;
  isPlatformClient: boolean;
  accessSpace: AccessSpace;
  revenues: CompanyRevenue[];
  incomes: CompanyIncome[];
  employeeCounts: CompanyEmployeeCount[];
  nameTrace?: NameTrace;
  legalName?: string;
  legalNameTrace?: NameTrace;
  listingStatus?: CompanyListingStatus;
  listingStatusSource?: string;
  country?: string;
  countrySource?: string;
  websiteURL?: string;
  websiteURLSource?: string;
  descriptions: CompanyDescription[];

  // Only available for team users
  secURLs?: CompanySecUrl[];
  cbondsLink?: CompanyCbondsLink;
  LEI?: string;
  CIK?: number;
  wikidataId?: string;
  wikiPages?: CompanyWikiPage[];
  productSets?: CompanyProductSet[];
  comments?: CompanyComment[];
  industry?: string;
  industrySource?: IndustrySource;
  VATIN?: string;
  ISINs?: SecurityIdentifier[];
  tickers?: SecurityIdentifier[];
  extraData?: CompanyExtraData[];
  alternativeNames?: CompanyAlternativeName[];
  parents?: CompanyRelation[];
  children?: CompanyRelation[];
  impactDriverType?: CompanyImpactDriverType;
  isModelled: boolean;
  // PLT-4790: PLG Enterprise "Draft mode". When `true`, customer-side input
  // editing on this profile is locked and DMA result pages are paywalled for
  // customer users; Upright-team users are unaffected. The flag is profile-
  // level (parallels `isModelled`) so it persists across variant generations
  // until QA actually ends and each subsidiary in a multi-profile group
  // (e.g. ERM) carries its own state.
  draftMode: boolean;
  // PLT-5806: PLG "Hide all results". When `true`, customer users on this
  // profile see every PLG dashboard results lens (DMA, Financial effects,
  // Climate) blurred/paywalled as a profile with no results yet, while the
  // input pages stay editable. Team users are unaffected. Profile-level
  // (parallels `draftMode`), so each subsidiary in a multi-profile group
  // carries its own state.
  hideAllResults: boolean;
}

export enum NameTrace {
  CLEANER = 'cleaner',
  MANUAL = 'manual',
  IMPORT = 'import',
  SECURITY = 'security',
}

export enum CountryTrace {
  DEDUCER = 'deducer',
  MANUAL = 'manual',
  GPT = 'upright-company-mapping-service',
}

export enum IndustrySource {
  INSIDEVIEW = 'insideview',
  IMPORT = 'import',
  MANUAL = 'manual',
}

export enum ExternalIdTrace {
  MATCHER = 'matcher',
  MANUAL = 'manual',
}

export interface CompanyCbondsLink {
  companyId: string;
  cbondsCompanyId: number;
  source: CbondsLinkSource;
}

// NOTE: if you add new Insideview sources, add them also to the list below
export enum CbondsLinkSource {
  MANUAL = 'manual',
  LEI = 'upright-lei',
  INSIDEVIEW_TICKER = 'insideview-ticker',
  MANUAL_ISIN = 'manual-isin',
  IMPORT = 'import',
}
const insideviewSources: CbondsLinkSource[] = [CbondsLinkSource.INSIDEVIEW_TICKER];

export function isInsideviewSource(sourceString: string) {
  return insideviewSources.includes(sourceString as CbondsLinkSource);
}

export interface CompanyIdAndName {
  id: string;
  name: string;
}

export interface CompanyById {
  [id: string]: Company;
}

export interface CompanyNamesById {
  [id: string]: string;
}

export type ByCompanyId<T> = {
  [key: string]: T;
};

export type ContextQuestionHash = Dictionary<ContextQuestion>;
export type ContextEmphasisHash = Dictionary<ContextEmphasis>;

export interface ContextQuestion {
  originalContext: string;
  hash: string;
  context: string;
  question: string;
  name?: string;
}

export interface ContextEmphasis extends ContextQuestion {
  emphasis: string;
}
